import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Card, PageHeader, Pill, EmptyState, Spinner } from "@/components/common";
import { toast } from "sonner";
import {
  Crown, Radar, Handshake, Hammer, Megaphone, Wallet, LineChart, FlaskConical,
  Play, Cpu, Activity, CheckCircle2, XCircle, ArrowRight, ShieldAlert, ScrollText, Send,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const ROOM_ICON = {
  command: Crown, intel: Radar, sales: Handshake, production: Hammer,
  marketing: Megaphone, treasury: Wallet, analytics: LineChart, research: FlaskConical,
};
const STATUS = {
  idle: { c: "text-slate-400", ring: "ring-slate-600", label: "Idle", dot: "bg-slate-500" },
  working: { c: "text-emerald-300", ring: "ring-emerald-400", label: "Working", dot: "bg-emerald-400" },
  waiting: { c: "text-amber-300", ring: "ring-amber-400", label: "Waiting", dot: "bg-amber-400" },
  blocked: { c: "text-orange-300", ring: "ring-orange-400", label: "Blocked", dot: "bg-orange-400" },
  error: { c: "text-red-300", ring: "ring-red-500", label: "Error", dot: "bg-red-500" },
  completed: { c: "text-blue-300", ring: "ring-blue-400", label: "Completed", dot: "bg-blue-400" },
};
const RUNNABLE = { overseer: "Run Coordination Cycle", scout: "Research Opportunities", sales: "Create Offer" };
const STAGES = ["Opportunity", "Qualified", "Offer Created", "Approval", "Contacted", "Interested", "Payment", "Production", "Quality Control", "Delivered", "Completed"];
const money = (n) => `$${Number(n || 0).toLocaleString()}`;

function Metric({ label, value, tone }) {
  return (
    <div className="px-4 py-3 rounded-lg bg-[#0c1117] border border-slate-800/80 min-w-[120px]" data-testid={`vault-metric-${label.toLowerCase().replace(/[ /]/g, "-")}`}>
      <div className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">{label}</div>
      <div className={`text-xl font-head font-bold ${tone || "text-slate-100"}`}>{value}</div>
    </div>
  );
}

function AgentNode({ agent, onClick }) {
  const s = STATUS[agent.status] || STATUS.idle;
  const Icon = ROOM_ICON[agent.room] || Cpu;
  return (
    <button onClick={() => onClick(agent)} data-testid={`vault-agent-${agent.key}`} className="group flex flex-col items-center gap-1.5 focus:outline-none">
      <div className={`relative w-14 h-14 rounded-full bg-[#0f1621] border border-slate-700 flex items-center justify-center ring-2 ${s.ring} ${agent.status === "working" ? "agent-working agent-ring-working" : ""}`}>
        <Icon className={`w-6 h-6 ${s.c}`} />
        <span className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-[#0f1621] ${s.dot}`} />
      </div>
      <div className="text-center leading-tight">
        <div className="text-[11px] font-semibold text-slate-200 group-hover:text-emerald-300 transition-colors">{agent.name}</div>
        <div className={`text-[9px] font-mono uppercase ${s.c}`}>{s.label}</div>
      </div>
    </button>
  );
}

export default function Vault() {
  const [state, setState] = useState(null);
  const [dash, setDash] = useState(null);
  const [opps, setOpps] = useState([]);
  const [offers, setOffers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [logs, setLogs] = useState([]);
  const [panel, setPanel] = useState(null);
  const [panelData, setPanelData] = useState(null);
  const [running, setRunning] = useState(null);
  const [deliverable, setDeliverable] = useState(null);

  const refresh = useCallback(async () => {
    const [s, d, o, of, or_, ap, lg] = await Promise.all([
      api.get("/vault/state"), api.get("/vault/dashboard"), api.get("/vault/opportunities"),
      api.get("/vault/offers"), api.get("/vault/orders"), api.get("/vault/approvals"), api.get("/vault/logs"),
    ].map((p) => p.catch(() => ({ data: null }))));
    if (s.data) setState(s.data);
    if (d.data) setDash(d.data);
    setOpps(o.data || []); setOffers(of.data || []); setOrders(or_.data || []);
    setApprovals(ap.data || []); setLogs(lg.data || []);
  }, []);

  useEffect(() => { refresh(); const t = setInterval(refresh, 8000); return () => clearInterval(t); }, [refresh]);

  const openPanel = async (agent) => {
    setPanel(agent); setPanelData(null);
    try { const { data } = await api.get(`/vault/agents/${agent.key}`); setPanelData(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const runAgent = async (key) => {
    setRunning(key);
    try {
      await api.post(`/vault/agents/${key}/run`);
      toast.success(`${key} completed its task.`);
      await refresh();
      if (panel?.key === key) { const { data } = await api.get(`/vault/agents/${key}`); setPanelData(data); }
    } catch (e) { toast.error(apiError(e)); }
    finally { setRunning(null); }
  };

  const buy = async (offer) => {
    try {
      const { data } = await api.post("/vault/offers/checkout", { offer_id: offer.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e, "Checkout failed")); }
  };

  const decide = async (id, decision) => {
    try { await api.post(`/vault/approvals/${id}/decision`, { decision }); toast.success(`Approval ${decision}d.`); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const orderAction = async (order, action) => {
    try { await api.post(`/vault/orders/${order.id}/${action}`); toast.success(action === "qc" ? "QC passed." : "Delivered & completed."); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const viewDeliverable = async (order) => {
    try { const { data } = await api.get(`/vault/deliverables/${order.id}`); setDeliverable(data); }
    catch (e) { toast.error(apiError(e, "No deliverable yet")); }
  };

  const agentByRoom = (rk) => (state?.agents || []).find((a) => a.room === rk);
  const pending = approvals.filter((a) => a.status === "pending");

  return (
    <>
      <PageHeader title="AI Vault" subtitle="Your underground AI workforce. Agents perform real tasks — research, offers, production — with human approval on money & outreach." testid="vault-header">
        <button data-testid="run-overseer-button" onClick={() => runAgent("overseer")} disabled={running} className="px-4 py-2 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 disabled:opacity-60 flex items-center gap-2">
          {running === "overseer" ? <Spinner label="Coordinating…" /> : <><Play className="w-4 h-4" /> Run Overseer Cycle</>}
        </button>
      </PageHeader>

      <div className="flex gap-3 overflow-x-auto pb-1" data-testid="vault-dashboard">
        <Metric label="Actual Revenue" value={money(dash?.actual_revenue)} tone="text-emerald-400" />
        <Metric label="Pending (Est)" value={money(dash?.pending_revenue)} tone="text-amber-300" />
        <Metric label="Customers" value={dash?.customers ?? "—"} />
        <Metric label="Active Jobs" value={dash?.active_jobs ?? "—"} />
        <Metric label="Active Agents" value={dash ? `${dash.active_agents}/${dash.agents}` : "—"} />
        <Metric label="Tasks OK" value={dash?.successful_tasks ?? "—"} tone="text-emerald-300" />
        <Metric label="Tasks Fail" value={dash?.failed_tasks ?? "—"} tone="text-red-300" />
        <Metric label="Opportunities" value={dash?.opportunities ?? "—"} />
        <Metric label="Conversion" value={dash ? `${dash.conversion_rate}%` : "—"} />
      </div>

      <Tabs defaultValue="vault">
        <TabsList className="bg-[#0c1117] border border-slate-800">
          <TabsTrigger value="vault" data-testid="tab-vault" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">Vault Map</TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">Revenue Pipeline</TabsTrigger>
          <TabsTrigger value="approvals" data-testid="tab-approvals" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">
            Approvals {pending.length > 0 && <span className="ml-1.5 text-[10px] px-1.5 rounded bg-amber-500/20 text-amber-300">{pending.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-300">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="vault" className="mt-4">
          <div className="vault-floor scanline rounded-2xl border border-slate-800 p-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 relative z-10">
              {(state?.rooms || []).map((room) => {
                const RoomIcon = ROOM_ICON[room.key] || Cpu;
                const agent = agentByRoom(room.key);
                return (
                  <div key={room.key} className="vault-room rounded-xl bg-[#0b1119]/80 border border-slate-800 hover:border-emerald-500/40 p-4 flex flex-col items-center gap-3 backdrop-blur-sm" data-testid={`vault-room-${room.key}`}>
                    <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-400">
                      <RoomIcon className="w-3.5 h-3.5 text-emerald-400/70" /> {room.name}
                    </div>
                    <div className="iso-base" />
                    <div className="min-h-[72px] flex items-center justify-center">
                      {agent ? <AgentNode agent={agent} onClick={openPanel} />
                        : <span className="text-[10px] text-slate-600 font-mono">EMPTY BAY</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-500">
            {Object.entries(STATUS).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${v.dot}`} /> {v.label}</span>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="pipeline" className="mt-4 space-y-5">
          <Card className="p-4" data-testid="pipeline-stages">
            <div className="flex items-center gap-1 overflow-x-auto pb-1">
              {STAGES.map((st, i) => {
                const count = [...opps, ...orders].filter((x) => x.stage === st).length;
                return (
                  <div key={st} className="flex items-center gap-1 shrink-0">
                    <div className={`px-2.5 py-1.5 rounded-md text-[11px] border ${count ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-slate-800/40 border-slate-700/50 text-slate-500"}`}>
                      {st} {count > 0 && <span className="font-bold">· {count}</span>}
                    </div>
                    {i < STAGES.length - 1 && <ArrowRight className="w-3 h-3 text-slate-700" />}
                  </div>
                );
              })}
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="p-5" data-testid="offers-list">
              <h3 className="font-head text-base font-semibold text-slate-100 mb-3">Offers — simulate a customer purchase (Stripe test)</h3>
              {offers.length === 0 ? <EmptyState icon={Handshake} title="No offers yet" hint="Run the Overseer cycle, then Sales, to generate an offer." /> :
                <div className="space-y-3">
                  {offers.map((o) => (
                    <div key={o.id} className="rounded-lg bg-[#0c1117] border border-slate-800 p-3" data-testid={`offer-${o.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div><div className="text-slate-100 font-medium text-sm">{o.name}</div><div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{o.description}</div></div>
                        <div className="text-emerald-400 font-head font-bold">{money(o.price)}</div>
                      </div>
                      <button data-testid={`buy-offer-${o.id}`} onClick={() => buy(o)} className="mt-3 w-full px-3 py-2 rounded-lg bg-emerald-500 text-slate-950 text-sm font-semibold hover:bg-emerald-400">Buy (test checkout)</button>
                    </div>
                  ))}
                </div>}
            </Card>

            <Card className="p-5" data-testid="orders-list">
              <h3 className="font-head text-base font-semibold text-slate-100 mb-3">Orders — production → QC → delivery</h3>
              {orders.length === 0 ? <EmptyState icon={Hammer} title="No orders yet" hint="Buy an offer to create a real paid order." /> :
                <div className="space-y-3">
                  {orders.map((o) => (
                    <div key={o.id} className="rounded-lg bg-[#0c1117] border border-slate-800 p-3" data-testid={`order-${o.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-200">{money(o.amount)} · {o.customer_email}</span>
                        <Pill tone={o.status === "completed" ? "emerald" : o.status === "pending" ? "amber" : "blue"}>{o.stage}</Pill>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {o.status === "production" && <button data-testid={`qc-${o.id}`} onClick={() => orderAction(o, "qc")} className="text-xs px-3 py-1.5 rounded bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700">Pass QC</button>}
                        {o.status === "quality_control" && <button data-testid={`deliver-${o.id}`} onClick={() => orderAction(o, "deliver")} className="text-xs px-3 py-1.5 rounded bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400">Deliver</button>}
                        {o.deliverable_id && <button data-testid={`view-deliverable-${o.id}`} onClick={() => viewDeliverable(o)} className="text-xs px-3 py-1.5 rounded bg-slate-800 text-slate-200 hover:bg-slate-700 border border-slate-700">View deliverable</button>}
                      </div>
                    </div>
                  ))}
                </div>}
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="approvals" className="mt-4">
          <Card className="p-5" data-testid="approvals-list">
            {pending.length === 0 ? <EmptyState icon={ShieldAlert} title="No pending approvals" hint="Money-out and external outreach actions will appear here for your sign-off." /> :
              <div className="space-y-3">
                {pending.map((a) => (
                  <div key={a.id} className="rounded-lg bg-[#0c1117] border border-amber-500/20 p-4" data-testid={`approval-${a.id}`}>
                    <div className="flex items-center gap-2"><Send className="w-4 h-4 text-amber-300" /><span className="text-slate-100 font-medium text-sm">{a.title}</span></div>
                    {a.payload?.outreach_draft && <pre className="mt-2 text-xs text-slate-400 whitespace-pre-wrap font-sans bg-[#070a0f] rounded p-3 border border-slate-800">{a.payload.outreach_draft}</pre>}
                    <div className="flex gap-2 mt-3">
                      <button data-testid={`approve-${a.id}`} onClick={() => decide(a.id, "approve")} className="px-3 py-1.5 rounded bg-emerald-500 text-slate-950 text-sm font-semibold hover:bg-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Approve</button>
                      <button data-testid={`reject-${a.id}`} onClick={() => decide(a.id, "reject")} className="px-3 py-1.5 rounded bg-red-950/60 text-red-300 text-sm border border-red-800/60 hover:bg-red-900/60 flex items-center gap-1"><XCircle className="w-4 h-4" /> Reject</button>
                    </div>
                  </div>
                ))}
              </div>}
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card className="p-5" data-testid="activity-feed">
            <h3 className="font-head text-base font-semibold text-slate-100 mb-3 flex items-center gap-2"><ScrollText className="w-4 h-4 text-emerald-400" /> Agent activity log</h3>
            {logs.length === 0 ? <EmptyState icon={Activity} title="No activity yet" hint="Run the Overseer cycle to kick off the workforce." /> :
              <div className="space-y-1.5 max-h-[460px] overflow-y-auto">
                {logs.map((l) => (
                  <div key={l.id} className="flex items-start gap-2 text-sm py-1.5 border-b border-slate-800/50" data-testid={`log-${l.id}`}>
                    <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${l.level === "success" ? "bg-emerald-400" : l.level === "error" ? "bg-red-400" : "bg-slate-500"}`} />
                    <span className="text-[11px] font-mono text-slate-500 uppercase w-20 shrink-0">{l.agent_key}</span>
                    <span className="text-slate-300">{l.message}</span>
                  </div>
                ))}
              </div>}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Agent operational panel */}
      <Dialog open={!!panel} onOpenChange={(o) => !o && setPanel(null)}>
        <DialogContent className="bg-[#0b1119] border-slate-800 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">{panel?.name} <Pill tone="slate">{panel?.role}</Pill></DialogTitle>
            <DialogDescription className="text-slate-400">Autonomy: {panel?.autonomy} · Room: {panel?.room}</DialogDescription>
          </DialogHeader>
          {!panelData ? <Spinner label="Loading agent…" /> : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                <Pill tone={panelData.agent.status === "working" ? "emerald" : panelData.agent.status === "error" ? "red" : "slate"}>{STATUS[panelData.agent.status]?.label}</Pill>
                {(panelData.agent.tools || []).map((t) => <Pill key={t} tone="blue">{t}</Pill>)}
                {panelData.revenue_attributed > 0 && <Pill tone="emerald">Revenue {money(panelData.revenue_attributed)}</Pill>}
              </div>
              {panelData.agent.current_task && <div className="text-sm text-slate-300">Current task: <span className="text-emerald-300">{panelData.agent.current_task}</span></div>}
              {RUNNABLE[panel.key] && (
                <button data-testid={`panel-run-${panel.key}`} onClick={() => runAgent(panel.key)} disabled={running} className="w-full px-4 py-2.5 rounded-lg bg-emerald-500 text-slate-950 font-semibold hover:bg-emerald-400 disabled:opacity-60 flex items-center justify-center gap-2">
                  {running === panel.key ? <Spinner label="Working…" /> : <><Play className="w-4 h-4" /> {RUNNABLE[panel.key]}</>}
                </button>
              )}
              <div>
                <div className="text-xs font-mono uppercase text-slate-500 mb-1">Recent tasks</div>
                <div className="space-y-1">
                  {panelData.tasks.length === 0 ? <div className="text-sm text-slate-600">No tasks yet.</div> :
                    panelData.tasks.slice(0, 8).map((t) => (
                      <div key={t.id} className="flex items-center justify-between text-sm py-1 border-b border-slate-800/50">
                        <span className="text-slate-300 truncate">{t.title}</span>
                        <Pill tone={t.status === "done" ? "emerald" : t.status === "error" ? "red" : "amber"}>{t.status}</Pill>
                      </div>
                    ))}
                </div>
              </div>
              <div>
                <div className="text-xs font-mono uppercase text-slate-500 mb-1">Recent actions</div>
                <div className="space-y-1">
                  {panelData.logs.slice(0, 8).map((l) => <div key={l.id} className="text-xs text-slate-400">· {l.message}</div>)}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Deliverable viewer */}
      <Dialog open={!!deliverable} onOpenChange={(o) => !o && setDeliverable(null)}>
        <DialogContent className="bg-[#0b1119] border-slate-800 text-slate-100 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-head">{deliverable?.title}</DialogTitle>
            <DialogDescription className="text-slate-400">{deliverable?.summary}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto text-sm text-slate-300 whitespace-pre-wrap bg-[#070a0f] rounded-lg p-4 border border-slate-800">{deliverable?.content}</div>
        </DialogContent>
      </Dialog>
    </>
  );
}
